import { useState } from 'react'
import { Search, Settings, X } from 'lucide-react'
import { widgetAPI } from '../../services/api'
import { useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'

interface GoogleSearchWidgetProps {
  widget: any
  isExpanded: boolean
}

const SEARCH_ENGINES = [
  { id: 'google', name: 'Google', url: 'https://www.google.com/search?q=' },
  { id: 'bing', name: 'Bing', url: 'https://www.bing.com/search?q=' },
  { id: 'duckduckgo', name: 'DuckDuckGo', url: 'https://duckduckgo.com/?q=' },
  { id: 'brave', name: 'Brave Search', url: 'https://search.brave.com/search?q=' },
  { id: 'ecosia', name: 'Ecosia', url: 'https://www.ecosia.org/search?q=' },
  { id: 'yahoo', name: 'Yahoo', url: 'https://search.yahoo.com/search?p=' },
  { id: 'amazon', name: 'Amazon', url: 'https://www.amazon.com/s?k=' },
]

const AI_ENGINES = [
  { id: 'chatgpt', name: 'ChatGPT', url: 'https://chat.openai.com/?q=' },
  { id: 'claude', name: 'Claude', url: 'https://claude.ai/new?q=' },
  { id: 'gemini', name: 'Google Gemini', url: 'https://gemini.google.com/?q=' },
  { id: 'perplexity', name: 'Perplexity AI', url: 'https://www.perplexity.ai/?q=' },
  { id: 'copilot', name: 'Microsoft Copilot', url: 'https://copilot.microsoft.com/?q=' },
  { id: 'you', name: 'You.com', url: 'https://you.com/search?q=' },
]

export default function GoogleSearchWidget({ widget, isExpanded }: GoogleSearchWidgetProps) {
  const [query, setQuery] = useState('')
  const [showSettings, setShowSettings] = useState(false)
  const queryClient = useQueryClient()
  
  const isAI = widget.app_slug === 'chatgpt-search' || widget.app_slug === 'claude-search'
  const isAmazon = widget.app_slug === 'amazon-search'
  const engines = isAI ? AI_ENGINES : SEARCH_ENGINES
  const defaultEngine = widget.app_slug === 'claude-search' ? 'claude'
    : widget.app_slug === 'chatgpt-search' ? 'chatgpt'
    : isAmazon ? 'amazon'
    : 'google'
  const currentEngine = widget.config?.engine || defaultEngine
  const selectedEngine = engines.find(e => e.id === currentEngine) || engines[0]

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (query.trim()) {
      const url = `${selectedEngine.url}${encodeURIComponent(query)}`
      window.open(url, '_blank')
      setQuery('')
    }
  }

  const handleEngineChange = async (engineId: string) => {
    try {
      const newConfig = {
        ...widget.config,
        engine: engineId
      }
      await widgetAPI.update(widget.id, {
        config_data: JSON.stringify(newConfig)
      })
      queryClient.invalidateQueries({ queryKey: ['widgets'] })
      toast.success('Search engine updated!')
      setShowSettings(false)
    } catch (error) {
      toast.error('Failed to update settings')
    }
  }

  const placeholder = isAI
    ? `Ask ${selectedEngine.name}...`
    : isAmazon
    ? `Search Amazon...`
    : `Search with ${selectedEngine.name}...`

  return (
    <div className="relative flex items-center justify-center h-full p-2">
      {/* Settings Button */}
      {!showSettings && (
        <button
          onClick={() => setShowSettings(true)}
          className="absolute top-2 right-2 p-1.5 hover:bg-gray-100 rounded-full transition z-10"
          title="Search Settings"
        >
          <Settings size={14} className="text-gray-500" />
        </button>
      )}

      {/* Settings Panel */}
      {showSettings && (
        <div 
          className="absolute inset-0 rounded-lg shadow-lg p-4 z-20 overflow-y-auto"
          style={{ backgroundColor: 'var(--color-widget-bg)' }}
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
              {isChatGPT ? 'AI Engine' : 'Search Engine'}
            </h3>
            <button
              onClick={() => setShowSettings(false)}
              className="p-1 rounded transition"
              style={{ color: 'var(--color-text-primary)' }}
            >
              <X size={18} />
            </button>
          </div>

          <div className="space-y-2">
            <p className="text-sm mb-3" style={{ color: 'var(--color-text-secondary)' }}>
              Choose your preferred {isChatGPT ? 'AI assistant' : 'search provider'}:
            </p>
            {engines.map((engine) => (
              <button
                key={engine.id}
                onClick={() => handleEngineChange(engine.id)}
                className={`w-full text-left px-4 py-3 rounded-lg border-2 transition ${
                  currentEngine === engine.id
                    ? 'bg-primary-50'
                    : 'hover:opacity-80'
                }`}
                style={{
                  borderColor: currentEngine === engine.id ? 'var(--color-primary)' : 'var(--color-border)',
                  backgroundColor: currentEngine === engine.id ? undefined : 'var(--color-surface)',
                }}
              >
                <div className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                  {engine.name}
                </div>
                <div className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                  {engine.url}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Search Form */}
      {!showSettings && (
        <form onSubmit={handleSearch} className="w-full max-w-2xl">
          <div className="relative">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={placeholder}
              className="w-full px-4 py-1.5 pl-10 border-2 border-gray-300 rounded-full focus:border-primary-500 focus:outline-none transition text-sm"
              style={{ borderColor: 'var(--color-border)' }}
            />
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={16}
            />
            <button
              type="submit"
              className="absolute right-1.5 top-1/2 transform -translate-y-1/2 px-4 py-1 text-white rounded-full transition text-sm"
              style={{ backgroundColor: 'var(--color-button-bg, #0183c6)' }}
            >
              Search
            </button>
          </div>
        </form>
      )}
    </div>
  )
}

